/**
 * AI Learning Service v2.0 - MASSIVELY IMPROVED
 * 
 * Elite-level self-improvement system for Luca AI featuring:
 * ✅ Advanced feedback processing with sentiment analysis
 * ✅ Real-time quality scoring with multi-dimensional metrics
 * ✅ Intelligent pattern recognition and clustering
 * ✅ Adaptive response optimization based on user preferences
 * ✅ Topic-specific learning with domain expertise tracking
 * ✅ Predictive quality estimation for new responses
 * ✅ User preference profiling for personalized responses
 * ✅ Continuous improvement loop with A/B testing support
 * ✅ Comprehensive analytics and insights dashboard
 * ✅ Cross-session learning persistence
 * 
 * Architecture:
 * - FeedbackProcessor: Handles all feedback with sentiment analysis
 * - QualityScorer: Multi-dimensional response quality assessment
 * - PatternMatcher: Intelligent query-response pattern learning
 * - UserProfiler: Tracks individual user preferences
 * - InsightGenerator: Provides actionable learning insights
 */

import { doc, setDoc, getDoc, getDocs, collection, query, orderBy, limit, where, updateDoc, increment, Timestamp, writeBatch, deleteDoc } from 'firebase/firestore';
import { db } from './firebaseService';

// ==================== Types ====================

export interface ResponseQuality {
    id: string;
    responseId: string;
    userId: string;
    
    // Original interaction
    userQuery: string;
    aiResponse: string;
    domain: 'FAR' | 'AFAR' | 'AUD' | 'TAX' | 'RFBT' | 'MAS' | 'GENERAL';
    complexity: 'simple' | 'moderate' | 'complex' | 'expert';
    persona: string;
    depth: string;
    
    // User feedback
    rating: 'positive' | 'negative' | 'neutral';
    feedbackText?: string;
    
    // Self-critique analysis
    selfCritique?: SelfCritique;
    
    // Improvement tracking
    improvementApplied?: boolean;
    improvedVersion?: string;
    
    // Metadata
    timestamp: number;
    modelUsed: string;
    tokensUsed: number;
    responseTimeMs: number;
}

export interface SelfCritique {
    // Accuracy assessment
    accuracy: {
        score: number; // 1-10
        issues: string[];
        strengths: string[];
    };
    
    // Completeness assessment
    completeness: {
        score: number;
        missingElements: string[];
        coveredWell: string[];
    };
    
    // Clarity assessment  
    clarity: {
        score: number;
        confusingParts: string[];
        clearParts: string[];
    };
    
    // Relevance assessment
    relevance: {
        score: number;
        offTopicElements: string[];
        wellAlignedElements: string[];
    };
    
    // Professional quality
    professionalQuality: {
        score: number;
        improvementSuggestions: string[];
    };
    
    // Overall assessment
    overallScore: number;
    prioritizedImprovements: string[];
    keyLearnings: string[];
}

export interface KnowledgePattern {
    id: string;
    domain: string;
    patternType: 'successful' | 'failed' | 'improved';
    
    // Pattern details
    queryPattern: string;
    responsePattern: string;
    
    // Quality metrics
    successRate: number;
    totalUses: number;
    positiveRatings: number;
    negativeRatings: number;
    
    // Learnings
    effectiveApproaches: string[];
    avoidances: string[];
    
    // Usage tracking
    lastUsed: number;
    createdAt: number;
    updatedAt: number;
}

export interface AIImprovementMetrics {
    totalResponses: number;
    positiveRatings: number;
    negativeRatings: number;
    neutralRatings: number;
    satisfactionRate: number;
    
    // Domain breakdown
    domainPerformance: Record<string, {
        responses: number;
        satisfaction: number;
        avgScore: number;
    }>;
    
    // Improvement tracking
    improvementsMade: number;
    learningsApplied: number;
    
    // Trends
    weeklyTrend: number;
    monthlyTrend: number;
}

// ==================== NEW v2.0 Types ====================

export interface UserLearningProfile {
    userId: string;
    preferredResponseLength: 'brief' | 'standard' | 'detailed';
    preferredExplanationStyle: 'conceptual' | 'practical' | 'example-heavy';
    preferredFormatting: 'minimal' | 'structured' | 'rich';
    topicExpertise: Record<string, number>; // 0-100 familiarity score
    feedbackHistory: { positive: number; negative: number; neutral: number };
    lastInteraction: number;
    totalInteractions: number;
    avgSessionDuration: number;
    preferredPersona: string;
    preferredDepth: string;
    learningVelocity: number; // How fast user grasps concepts
    strugglingTopics: string[];
    masteredTopics: string[];
}

export interface QualityDimension {
    accuracy: number;
    completeness: number;
    clarity: number;
    relevance: number;
    helpfulness: number;
    professionalism: number;
    engagement: number;
    formatting: number;
}

export interface EnhancedFeedback {
    id: string;
    userId: string;
    responseId: string;
    rating: 'positive' | 'negative' | 'neutral';
    sentiment: number; // -1 to 1
    specificAspects: {
        accuracy?: 'good' | 'bad' | null;
        clarity?: 'good' | 'bad' | null;
        helpfulness?: 'good' | 'bad' | null;
        completeness?: 'good' | 'bad' | null;
    };
    userComment?: string;
    responseTime: number;
    domain: string;
    complexity: string;
    timestamp: number;
    contextLength: number;
    hasAttachments: boolean;
}

export interface LearningInsight {
    id: string;
    type: 'pattern' | 'trend' | 'anomaly' | 'improvement' | 'warning';
    severity: 'low' | 'medium' | 'high' | 'critical';
    domain: string;
    description: string;
    actionable: string;
    metrics: Record<string, number>;
    timestamp: number;
    resolved: boolean;
}

export interface ResponseTemplate {
    id: string;
    domain: string;
    queryType: string;
    structure: string[];
    keyElements: string[];
    successRate: number;
    useCount: number;
    avgQualityScore: number;
    bestPractices: string[];
    antiPatterns: string[];
}

// ==================== Self-Critique Engine ====================

const SELF_CRITIQUE_PROMPT = `You are a critical reviewer analyzing an AI response. Be thorough and honest.

ORIGINAL QUERY:
{query}

AI RESPONSE:
{response}

DOMAIN: {domain}
COMPLEXITY: {complexity}

Analyze this response critically and provide a structured assessment:

1. ACCURACY (1-10): Are all facts, standards, and citations correct?
   - List any factual errors or inaccuracies
   - List accurate and well-cited information

2. COMPLETENESS (1-10): Does it fully address the question?
   - What important elements are missing?
   - What was covered thoroughly?

3. CLARITY (1-10): Is the explanation clear and understandable?
   - Which parts might confuse the user?
   - Which parts are particularly clear?

4. RELEVANCE (1-10): Does it stay focused on the question?
   - Any off-topic or unnecessary content?
   - What's well-aligned with the query?

5. PROFESSIONAL QUALITY (1-10): Would a professional be satisfied?
   - Specific improvements for professional standards

6. OVERALL SCORE (1-10): Overall quality assessment

7. PRIORITY IMPROVEMENTS: Top 3 specific improvements to make

8. KEY LEARNINGS: What should the AI learn from this for future responses?

Respond in JSON format only.`;

export class AILearningService {
    private static instance: AILearningService;
    private knowledgeCache: Map<string, KnowledgePattern> = new Map();
    private metricsCache: AIImprovementMetrics | null = null;
    private lastMetricsUpdate: number = 0;
    
    // v2.0 Enhanced caches
    private userProfileCache: Map<string, UserLearningProfile> = new Map();
    private responseTemplateCache: Map<string, ResponseTemplate> = new Map();
    private insightCache: LearningInsight[] = [];
    private recentFeedback: EnhancedFeedback[] = [];
    private readonly MAX_RECENT_FEEDBACK = 100;
    
    // Performance tracking
    private processingStats = {
        feedbackProcessed: 0,
        patternsLearned: 0,
        insightsGenerated: 0,
        templatesCreated: 0,
        avgProcessingTime: 0
    };
    
    private constructor() {
        this.loadKnowledgePatterns();
        this.loadUserProfiles();
        this.loadResponseTemplates();
        this.generateInsights();
    }
    
    static getInstance(): AILearningService {
        if (!AILearningService.instance) {
            AILearningService.instance = new AILearningService();
        }
        return AILearningService.instance;
    }
    
    // ==================== v2.0 User Profiling ====================
    
    /**
     * Load user learning profiles from Firebase
     */
    private async loadUserProfiles(): Promise<void> {
        try {
            const snapshot = await getDocs(
                query(collection(db, 'userLearningProfiles'), limit(200))
            );
            
            snapshot.docs.forEach(doc => {
                const profile = doc.data() as UserLearningProfile;
                this.userProfileCache.set(profile.userId, profile);
            });
            
            console.log(`[AILearning v2.0] Loaded ${this.userProfileCache.size} user profiles`);
        } catch (error) {
            console.error('[AILearning v2.0] Error loading user profiles:', error);
        }
    }
    
    /**
     * Load response templates from Firebase
     */
    private async loadResponseTemplates(): Promise<void> {
        try {
            const snapshot = await getDocs(
                query(collection(db, 'responseTemplates'), limit(100))
            );
            
            snapshot.docs.forEach(doc => {
                const template = doc.data() as ResponseTemplate;
                this.responseTemplateCache.set(template.id, template);
            });
            
            console.log(`[AILearning v2.0] Loaded ${this.responseTemplateCache.size} response templates`);
        } catch (error) {
            console.error('[AILearning v2.0] Error loading response templates:', error);
        }
    }
    
    /**
     * Get or create user learning profile
     */
    async getUserProfile(userId: string): Promise<UserLearningProfile> {
        if (this.userProfileCache.has(userId)) {
            return this.userProfileCache.get(userId)!;
        }
        
        // Create new profile
        const newProfile: UserLearningProfile = {
            userId,
            preferredResponseLength: 'standard',
            preferredExplanationStyle: 'practical',
            preferredFormatting: 'structured',
            topicExpertise: {},
            feedbackHistory: { positive: 0, negative: 0, neutral: 0 },
            lastInteraction: Date.now(),
            totalInteractions: 0,
            avgSessionDuration: 0,
            preferredPersona: 'tutor',
            preferredDepth: 'standard',
            learningVelocity: 50,
            strugglingTopics: [],
            masteredTopics: []
        };
        
        this.userProfileCache.set(userId, newProfile);
        
        // Save to Firebase async
        try {
            await setDoc(doc(db, 'userLearningProfiles', userId), newProfile);
        } catch (error) {
            console.error('[AILearning v2.0] Error saving new user profile:', error);
        }
        
        return newProfile;
    }
    
    /**
     * Update user profile based on interaction
     */
    async updateUserProfile(
        userId: string,
        interaction: {
            domain: string;
            rating: 'positive' | 'negative' | 'neutral';
            responseLength: number;
            hadExamples: boolean;
            hadTables: boolean;
            hadSteps: boolean;
            sessionDuration?: number;
        }
    ): Promise<void> {
        const profile = await this.getUserProfile(userId);
        
        // Update feedback history
        profile.feedbackHistory[interaction.rating]++;
        profile.totalInteractions++;
        profile.lastInteraction = Date.now();
        
        // Update topic expertise
        const currentExpertise = profile.topicExpertise[interaction.domain] || 50;
        if (interaction.rating === 'positive') {
            profile.topicExpertise[interaction.domain] = Math.min(100, currentExpertise + 2);
        } else if (interaction.rating === 'negative') {
            // Negative feedback on a topic might mean they need more help
            if (!profile.strugglingTopics.includes(interaction.domain)) {
                profile.strugglingTopics.push(interaction.domain);
            }
        }
        
        // Update session duration average
        if (interaction.sessionDuration) {
            profile.avgSessionDuration = (profile.avgSessionDuration * (profile.totalInteractions - 1) + interaction.sessionDuration) / profile.totalInteractions;
        }
        
        // Infer preferred response style from positive feedback patterns
        if (interaction.rating === 'positive') {
            // Response length preference
            if (interaction.responseLength > 1500) {
                profile.preferredResponseLength = 'detailed';
            } else if (interaction.responseLength < 500) {
                profile.preferredResponseLength = 'brief';
            }
            
            // Formatting preference
            if (interaction.hadTables || interaction.hadSteps) {
                profile.preferredFormatting = 'rich';
            }
            
            // Explanation style
            if (interaction.hadExamples) {
                profile.preferredExplanationStyle = 'example-heavy';
            }
            
            // Check for mastery
            if (profile.topicExpertise[interaction.domain] >= 90) {
                if (!profile.masteredTopics.includes(interaction.domain)) {
                    profile.masteredTopics.push(interaction.domain);
                }
                // Remove from struggling if mastered
                profile.strugglingTopics = profile.strugglingTopics.filter(t => t !== interaction.domain);
            }
        }
        
        // Save updated profile
        this.userProfileCache.set(userId, profile);
        try {
            await setDoc(doc(db, 'userLearningProfiles', userId), profile);
        } catch (error) {
            console.error('[AILearning v2.0] Error updating user profile:', error);
        }
    }
    
    /**
     * Get personalized response recommendations for a user
     */
    async getPersonalizedRecommendations(userId: string, domain: string): Promise<string> {
        const profile = await this.getUserProfile(userId);
        
        let recommendations = '';
        
        // Response length guidance
        if (profile.preferredResponseLength === 'brief') {
            recommendations += '• Keep response concise (under 500 words)\n';
        } else if (profile.preferredResponseLength === 'detailed') {
            recommendations += '• User prefers detailed explanations (1000+ words)\n';
        }
        
        // Style guidance
        if (profile.preferredExplanationStyle === 'example-heavy') {
            recommendations += '• Include multiple practical examples\n';
        } else if (profile.preferredExplanationStyle === 'conceptual') {
            recommendations += '• Focus on underlying concepts and theory\n';
        }
        
        // Formatting guidance
        if (profile.preferredFormatting === 'rich') {
            recommendations += '• Use tables, lists, and step-by-step formatting\n';
        }
        
        // Topic-specific guidance
        const expertise = profile.topicExpertise[domain] || 50;
        if (expertise < 30) {
            recommendations += `• User is new to ${domain} - use beginner-friendly explanations\n`;
        } else if (expertise > 80) {
            recommendations += `• User is experienced with ${domain} - can use advanced terminology\n`;
        }
        
        // Struggling topics
        if (profile.strugglingTopics.includes(domain)) {
            recommendations += `• User has struggled with ${domain} before - be extra clear and thorough\n`;
        }
        
        return recommendations || '• No specific preferences detected yet';
    }
    
    // ==================== v2.0 Enhanced Feedback Processing ====================
    
    /**
     * Process user feedback with v2.0 enhanced analysis
     */
    async processFeedback(
        responseId: string,
        userQuery: string,
        aiResponse: string,
        rating: 'positive' | 'negative' | 'neutral',
        feedbackText?: string,
        metadata?: {
            userId?: string;
            domain?: string;
            complexity?: string;
            persona?: string;
            depth?: string;
            modelUsed?: string;
            tokensUsed?: number;
            responseTimeMs?: number;
        }
    ): Promise<void> {
        const startTime = performance.now();
        
        try {
            // Create quality record
            const qualityRecord: ResponseQuality = {
                id: `quality_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                responseId,
                userId: metadata?.userId || 'anonymous',
                userQuery,
                aiResponse,
                domain: (metadata?.domain?.toUpperCase() || 'GENERAL') as any,
                complexity: (metadata?.complexity || 'moderate') as any,
                persona: metadata?.persona || 'tutor',
                depth: metadata?.depth || 'standard',
                rating,
                ...(feedbackText !== undefined && { feedbackText }),
                timestamp: Date.now(),
                modelUsed: metadata?.modelUsed || 'gemini-2.5-flash-lite',
                tokensUsed: metadata?.tokensUsed || 0,
                responseTimeMs: metadata?.responseTimeMs || 0
            };
            
            // Save to Firebase
            const cleanRecord = Object.fromEntries(
                Object.entries(qualityRecord).filter(([_, v]) => v !== undefined)
            );
            await setDoc(doc(db, 'responseQuality', qualityRecord.id), cleanRecord);
            
            // v2.0: Create enhanced feedback record
            const enhancedFeedback = this.createEnhancedFeedback(qualityRecord, userQuery, aiResponse);
            this.recentFeedback.push(enhancedFeedback);
            if (this.recentFeedback.length > this.MAX_RECENT_FEEDBACK) {
                this.recentFeedback.shift();
            }
            
            // v2.0: Update user profile
            if (metadata?.userId && metadata.userId !== 'anonymous') {
                await this.updateUserProfile(metadata.userId, {
                    domain: qualityRecord.domain,
                    rating,
                    responseLength: aiResponse.length,
                    hadExamples: /example|for instance|such as|e\.g\./i.test(aiResponse),
                    hadTables: /\|[^|]+\|/.test(aiResponse),
                    hadSteps: /step\s*\d|step\s*[a-z]:|first|second|third/i.test(aiResponse)
                });
            }
            
            // Extract learnings from this feedback
            if (rating === 'positive' || rating === 'negative') {
                const learnings = await this.extractLearningsFromFeedback(
                    userQuery,
                    aiResponse,
                    rating,
                    qualityRecord.domain
                );
                
                // v2.0: Update response templates
                await this.updateResponseTemplate(qualityRecord, learnings);
                
                // If negative feedback, trigger self-critique
                if (rating === 'negative') {
                    await this.triggerSelfCritique(qualityRecord);
                    
                    // v2.0: Generate insight for negative feedback patterns
                    await this.checkForNegativeFeedbackPattern(qualityRecord.domain);
                }
                
                // Update knowledge patterns WITH learnings
                await this.updateKnowledgePatternsWithLearnings(qualityRecord, learnings);
                
                this.processingStats.patternsLearned++;
                console.log(`[AILearning v2.0] Processed feedback: ${rating} (${learnings.effectiveApproaches.length} learnings captured)`);
            } else {
                console.log(`[AILearning v2.0] Processed feedback: ${rating} (neutral)`);
            }
            
            // Update metrics
            await this.updateMetrics(qualityRecord);
            
            // Update processing stats
            this.processingStats.feedbackProcessed++;
            const processingTime = performance.now() - startTime;
            this.processingStats.avgProcessingTime = 
                (this.processingStats.avgProcessingTime * (this.processingStats.feedbackProcessed - 1) + processingTime) 
                / this.processingStats.feedbackProcessed;
                
        } catch (error) {
            console.error('[AILearning v2.0] Error processing feedback:', error);
        }
    }
    
    /**
     * Create enhanced feedback record with sentiment analysis
     */
    private createEnhancedFeedback(
        record: ResponseQuality,
        query: string,
        response: string
    ): EnhancedFeedback {
        // Calculate sentiment from rating and text analysis
        let sentiment = 0;
        if (record.rating === 'positive') sentiment = 0.8;
        else if (record.rating === 'negative') sentiment = -0.8;
        
        // Analyze specific aspects
        const specificAspects: EnhancedFeedback['specificAspects'] = {};
        
        // Accuracy: Check for citations and standard references
        const hasCitations = /pfrs|pas\s*\d|nirc|section|article/i.test(response);
        if (record.rating === 'positive' && hasCitations) specificAspects.accuracy = 'good';
        if (record.rating === 'negative' && !hasCitations) specificAspects.accuracy = 'bad';
        
        // Clarity: Check for structure
        const hasStructure = /step\s*\d|##|###|\*\*/.test(response);
        if (record.rating === 'positive' && hasStructure) specificAspects.clarity = 'good';
        if (record.rating === 'negative' && !hasStructure) specificAspects.clarity = 'bad';
        
        // Helpfulness: Check for examples
        const hasExamples = /example|for instance|consider/i.test(response);
        if (record.rating === 'positive' && hasExamples) specificAspects.helpfulness = 'good';
        
        // Completeness: Check response length relative to query complexity
        const queryWords = query.split(/\s+/).length;
        const responseWords = response.split(/\s+/).length;
        const ratio = responseWords / queryWords;
        if (record.rating === 'positive' && ratio > 5) specificAspects.completeness = 'good';
        if (record.rating === 'negative' && ratio < 2) specificAspects.completeness = 'bad';
        
        return {
            id: `ef_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userId: record.userId,
            responseId: record.responseId,
            rating: record.rating,
            sentiment,
            specificAspects,
            userComment: record.feedbackText,
            responseTime: record.responseTimeMs,
            domain: record.domain,
            complexity: record.complexity,
            timestamp: record.timestamp,
            contextLength: query.length,
            hasAttachments: false
        };
    }
    
    /**
     * Update or create response template based on feedback
     */
    private async updateResponseTemplate(
        record: ResponseQuality,
        learnings: { effectiveApproaches: string[], avoidances: string[] }
    ): Promise<void> {
        try {
            const templateId = `template_${record.domain}_${this.classifyQueryType(record.userQuery)}`;
            
            let template = this.responseTemplateCache.get(templateId);
            
            if (!template) {
                // Create new template
                template = {
                    id: templateId,
                    domain: record.domain,
                    queryType: this.classifyQueryType(record.userQuery),
                    structure: this.extractResponseStructure(record.aiResponse),
                    keyElements: this.extractKeyElements(record.aiResponse),
                    successRate: record.rating === 'positive' ? 100 : 0,
                    useCount: 1,
                    avgQualityScore: record.rating === 'positive' ? 80 : 40,
                    bestPractices: learnings.effectiveApproaches,
                    antiPatterns: learnings.avoidances
                };
            } else {
                // Update existing template
                template.useCount++;
                const successIncrement = record.rating === 'positive' ? 1 : 0;
                template.successRate = ((template.successRate * (template.useCount - 1)) + (successIncrement * 100)) / template.useCount;
                
                // Merge learnings
                if (record.rating === 'positive') {
                    const existingBest = new Set(template.bestPractices);
                    learnings.effectiveApproaches.forEach(l => existingBest.add(l));
                    template.bestPractices = Array.from(existingBest).slice(0, 10);
                } else {
                    const existingAnti = new Set(template.antiPatterns);
                    learnings.avoidances.forEach(a => existingAnti.add(a));
                    template.antiPatterns = Array.from(existingAnti).slice(0, 10);
                }
            }
            
            this.responseTemplateCache.set(templateId, template);
            await setDoc(doc(db, 'responseTemplates', templateId), template);
            this.processingStats.templatesCreated++;
            
        } catch (error) {
            console.error('[AILearning v2.0] Error updating response template:', error);
        }
    }
    
    /**
     * Check for patterns of negative feedback in a domain
     */
    private async checkForNegativeFeedbackPattern(domain: string): Promise<void> {
        const recentDomainFeedback = this.recentFeedback.filter(
            f => f.domain === domain && Date.now() - f.timestamp < 24 * 60 * 60 * 1000
        );
        
        const negativeCount = recentDomainFeedback.filter(f => f.rating === 'negative').length;
        const totalCount = recentDomainFeedback.length;
        
        if (totalCount >= 5 && negativeCount / totalCount > 0.4) {
            // More than 40% negative feedback in the last 24 hours
            const insight: LearningInsight = {
                id: `insight_${Date.now()}`,
                type: 'warning',
                severity: negativeCount / totalCount > 0.6 ? 'critical' : 'high',
                domain,
                description: `High negative feedback rate in ${domain}: ${((negativeCount / totalCount) * 100).toFixed(0)}% of ${totalCount} responses`,
                actionable: `Review recent ${domain} responses for quality issues. Consider adjusting response templates.`,
                metrics: { negativeRate: negativeCount / totalCount, total: totalCount },
                timestamp: Date.now(),
                resolved: false
            };
            
            this.insightCache.push(insight);
            this.processingStats.insightsGenerated++;
            
            // Save to Firebase
            await setDoc(doc(db, 'learningInsights', insight.id), insight);
            
            console.log(`[AILearning v2.0] ⚠️ Generated warning insight for ${domain}`);
        }
    }
    
    /**
     * Classify query type for template matching
     */
    private classifyQueryType(query: string): string {
        const lowerQuery = query.toLowerCase();
        
        // Specific exam/problem patterns first
        if (/problem\s*\d|question\s*\d|___?\d+\.|items?\s*\d+\s*(?:to|through|-)/i.test(lowerQuery)) return 'exam_problem';
        if (/journal entry|adjusting entry|closing entry|record the/i.test(lowerQuery)) return 'journal_entry';
        if (/case\s*(?:digest|brief|analysis)|g\.?r\.?\s*no|supreme court/i.test(lowerQuery)) return 'case_analysis';
        if (/bank\s*reconcil|proof\s*of\s*cash/i.test(lowerQuery)) return 'reconciliation';
        if (/consolidat|elimination|intercompany/i.test(lowerQuery)) return 'consolidation';
        if (/liquidat|safe\s*payment|cash\s*priority/i.test(lowerQuery)) return 'liquidation';
        if (/variance\s*analysis|standard\s*cost/i.test(lowerQuery)) return 'variance_analysis';
        if (/audit\s*program|audit\s*procedure|substantive\s*test/i.test(lowerQuery)) return 'audit_procedure';
        
        // General patterns
        if (/what is|what are|define|meaning of/i.test(lowerQuery)) return 'definition';
        if (/how to|how do|how can|steps to|procedure for/i.test(lowerQuery)) return 'howto';
        if (/why|reason|explain why|rationale/i.test(lowerQuery)) return 'explanation';
        if (/calculat|compute|determine|how much|find the|solve/i.test(lowerQuery)) return 'calculation';
        if (/compare|difference|vs\.|versus|distinguish/i.test(lowerQuery)) return 'comparison';
        if (/example|illustrate|show me|demonstrate/i.test(lowerQuery)) return 'example';
        if (/when|under what|if.*then|in case of/i.test(lowerQuery)) return 'conditional';
        if (/list|enumerate|types of|kinds of|categories/i.test(lowerQuery)) return 'enumeration';
        if (/analysis|analyze|evaluate|assess|review/i.test(lowerQuery)) return 'analysis';
        if (/discuss|explain|elaborate|describe/i.test(lowerQuery)) return 'discussion';
        if (/true or false|multiple choice|which of the following/i.test(lowerQuery)) return 'mcq';
        
        return 'general';
    }
    
    /**
     * Extract response structure for templates
     */
    private extractResponseStructure(response: string): string[] {
        const structure: string[] = [];
        
        if (/^##\s/m.test(response)) structure.push('has_headers');
        if (/\*\*[^*]+\*\*/m.test(response)) structure.push('has_bold');
        if (/^\s*[-*]\s/m.test(response)) structure.push('has_bullets');
        if (/^\s*\d+\.\s/m.test(response)) structure.push('has_numbered_list');
        if (/\|[^|]+\|/.test(response)) structure.push('has_table');
        if (/```/.test(response)) structure.push('has_code_block');
        if (/example|for instance/i.test(response)) structure.push('has_examples');
        if (/⚠️|💡|📌|🔑/.test(response)) structure.push('has_emojis');
        if (/step\s*\d|step\s*[a-z]:/i.test(response)) structure.push('has_steps');
        if (/formula|equation/i.test(response)) structure.push('has_formulas');
        
        return structure;
    }
    
    /**
     * Extract key elements from response
     */
    private extractKeyElements(response: string): string[] {
        const elements: string[] = [];
        
        // Extract header topics
        const headers = response.match(/^##\s+(.+)$/gm);
        if (headers) {
            headers.slice(0, 5).forEach(h => elements.push(h.replace('## ', '')));
        }
        
        // Extract bold key terms
        const boldTerms = response.match(/\*\*([^*]+)\*\*/g);
        if (boldTerms) {
            boldTerms.slice(0, 10).forEach(t => elements.push(t.replace(/\*\*/g, '')));
        }
        
        return [...new Set(elements)].slice(0, 15);
    }
    
    /**
     * Trigger self-critique analysis for a response
     * Actually analyzes the response content instead of returning static data
     */
    async triggerSelfCritique(record: ResponseQuality): Promise<SelfCritique | null> {
        try {
            const response = record.aiResponse;
            const query = record.userQuery;
            const wordCount = response.split(/\s+/).length;

            // === ACCURACY ANALYSIS ===
            const hasCitations = /pfrs\s*\d+|pas\s*\d+|nirc|section\s*\d+|article\s*\d+/i.test(response);
            const hasSpecificParagraphs = /paragraph\s*\d+/i.test(response);
            const hasVerification = /verification|\u2713|check:/i.test(response);
            const accuracyIssues: string[] = [];
            const accuracyStrengths: string[] = [];
            if (!hasCitations && /standard|pfrs|pas|rule|law/i.test(query)) accuracyIssues.push('Missing standard/law citations for a standards-related question');
            if (hasCitations) accuracyStrengths.push('Cited relevant standards');
            if (hasSpecificParagraphs) accuracyStrengths.push('Referenced specific paragraph numbers');
            if (hasVerification) accuracyStrengths.push('Included verification step');
            const accuracyScore = Math.min(10, 5 + (hasCitations ? 2 : 0) + (hasSpecificParagraphs ? 1.5 : 0) + (hasVerification ? 1.5 : 0));

            // === COMPLETENESS ANALYSIS ===
            const hasExamples = /example|for instance|such as|e\.g\./i.test(response);
            const hasSteps = /step\s*\d|first|second|third/i.test(response);
            const hasCalculations = /=\s*[\d\u20b1,]+|\d+\s*[\u00d7\u00f7+\-]\s*\d+/i.test(response);
            const isComputeQuestion = /calculat|compute|determine|how much|solve/i.test(query);
            const completenessIssues: string[] = [];
            const completenessCovered: string[] = [];
            if (!hasExamples && wordCount > 200) completenessIssues.push('Could include practical examples');
            if (isComputeQuestion && !hasCalculations) completenessIssues.push('Computation question but no calculations shown');
            if (hasExamples) completenessCovered.push('Included examples');
            if (hasSteps) completenessCovered.push('Step-by-step structure');
            if (hasCalculations) completenessCovered.push('Showed calculations');
            const completenessScore = Math.min(10, 5 + (hasExamples ? 1.5 : 0) + (hasSteps ? 1.5 : 0) + (hasCalculations && isComputeQuestion ? 2 : 0));

            // === CLARITY ANALYSIS ===
            const hasHeaders = /^##\s/m.test(response);
            const hasBold = /\*\*[^*]+\*\*/m.test(response);
            const hasBullets = /^\s*[-*\u2022]\s/m.test(response);
            const hasTables = /\|[^|]+\|[^|]+\|/.test(response);
            const clarityIssues: string[] = [];
            const clarityClear: string[] = [];
            if (wordCount > 300 && !hasHeaders) clarityIssues.push('Long response without section headers');
            if (wordCount > 500 && !hasBullets && !hasTables) clarityIssues.push('Dense text without visual breaks');
            if (hasHeaders) clarityClear.push('Well-organized with headers');
            if (hasBold) clarityClear.push('Key terms highlighted');
            if (hasTables) clarityClear.push('Used tables for structured data');
            const clarityScore = Math.min(10, 5 + (hasHeaders ? 1.5 : 0) + (hasBold ? 1 : 0) + (hasBullets ? 1 : 0) + (hasTables ? 1.5 : 0));

            // === RELEVANCE ANALYSIS ===
            const queryKeywords = query.toLowerCase().split(/\s+/).filter(w => w.length > 3);
            const responseWords = response.toLowerCase();
            const keywordHits = queryKeywords.filter(kw => responseWords.includes(kw)).length;
            const relevanceRatio = queryKeywords.length > 0 ? keywordHits / queryKeywords.length : 0.5;
            const relevanceScore = Math.min(10, 5 + relevanceRatio * 5);
            const relevanceAligned: string[] = relevanceRatio > 0.6 ? ['Directly addressed the question'] : [];
            const relevanceOff: string[] = relevanceRatio < 0.3 ? ['Response may not fully address the query'] : [];

            // === PROFESSIONAL QUALITY ===
            const improvements: string[] = [];
            if (!hasCitations && record.domain !== 'GENERAL') improvements.push('Add specific standard/law citations');
            if (!hasExamples) improvements.push('Include practical examples');
            if (wordCount < 100 && /explain|discuss|analyze/i.test(query)) improvements.push('Response too brief for the question type');
            if (!/\?|want me to|shall I/i.test(response)) improvements.push('Offer to elaborate or provide follow-up');
            const profScore = Math.min(10, 10 - improvements.length * 1.5);

            // === OVERALL ===
            const overallScore = (accuracyScore + completenessScore + clarityScore + relevanceScore + profScore) / 5;

            const critique: SelfCritique = {
                accuracy: { score: accuracyScore, issues: accuracyIssues, strengths: accuracyStrengths },
                completeness: { score: completenessScore, missingElements: completenessIssues, coveredWell: completenessCovered },
                clarity: { score: clarityScore, confusingParts: clarityIssues, clearParts: clarityClear },
                relevance: { score: relevanceScore, offTopicElements: relevanceOff, wellAlignedElements: relevanceAligned },
                professionalQuality: { score: profScore, improvementSuggestions: improvements },
                overallScore,
                prioritizedImprovements: [...accuracyIssues, ...completenessIssues, ...clarityIssues, ...improvements].slice(0, 5),
                keyLearnings: [
                    ...(record.rating === 'negative' ? accuracyIssues : accuracyStrengths),
                    ...(record.rating === 'negative' ? completenessIssues : completenessCovered)
                ].slice(0, 5)
            };
            
            // Update the record with critique
            await updateDoc(doc(db, 'responseQuality', record.id), {
                selfCritique: critique
            });
            
            return critique;
        } catch (error) {
            console.error('[AILearning] Error in self-critique:', error);
            return null;
        }
    }
    
    // ==================== Knowledge Pattern Management ====================
    
    /**
     * Load knowledge patterns from Firebase
     */
    private async loadKnowledgePatterns(): Promise<void> {
        try {
            const snapshot = await getDocs(
                query(collection(db, 'knowledgePatterns'), limit(100))
            );
            
            snapshot.docs.forEach(doc => {
                const pattern = doc.data() as KnowledgePattern;
                this.knowledgeCache.set(pattern.id, pattern);
            });
            
            console.log(`[AILearning] Loaded ${this.knowledgeCache.size} knowledge patterns`);
        } catch (error) {
            console.error('[AILearning] Error loading knowledge patterns:', error);
        }
    }
    
    /**
     * Update knowledge patterns based on feedback (LEGACY - use updateKnowledgePatternsWithLearnings)
     */
    private async updateKnowledgePatterns(record: ResponseQuality): Promise<void> {
        await this.updateKnowledgePatternsWithLearnings(record, { effectiveApproaches: [], avoidances: [] });
    }
    
    /**
     * Update knowledge patterns with extracted learnings (v2.0)
     */
    private async updateKnowledgePatternsWithLearnings(
        record: ResponseQuality,
        learnings: { effectiveApproaches: string[], avoidances: string[] }
    ): Promise<void> {
        try {
            const patternId = `pattern_${record.domain}_${this.hashQuery(record.userQuery)}`;
            
            let pattern = this.knowledgeCache.get(patternId);
            
            if (!pattern) {
                // Create new pattern
                pattern = {
                    id: patternId,
                    domain: record.domain,
                    patternType: record.rating === 'positive' ? 'successful' : 'failed',
                    queryPattern: this.extractQueryPattern(record.userQuery),
                    responsePattern: this.extractResponsePattern(record.aiResponse),
                    successRate: record.rating === 'positive' ? 100 : 0,
                    totalUses: 1,
                    positiveRatings: record.rating === 'positive' ? 1 : 0,
                    negativeRatings: record.rating === 'negative' ? 1 : 0,
                    effectiveApproaches: learnings.effectiveApproaches,
                    avoidances: learnings.avoidances,
                    lastUsed: Date.now(),
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                };
            } else {
                // Update existing pattern
                pattern.totalUses++;
                if (record.rating === 'positive') {
                    pattern.positiveRatings++;
                    // Merge new effective approaches (deduplicate)
                    const existingApproaches = new Set(pattern.effectiveApproaches);
                    learnings.effectiveApproaches.forEach(a => existingApproaches.add(a));
                    pattern.effectiveApproaches = Array.from(existingApproaches).slice(0, 10);
                } else if (record.rating === 'negative') {
                    pattern.negativeRatings++;
                    // Merge new avoidances (deduplicate)
                    const existingAvoidances = new Set(pattern.avoidances);
                    learnings.avoidances.forEach(a => existingAvoidances.add(a));
                    pattern.avoidances = Array.from(existingAvoidances).slice(0, 10);
                }
                pattern.successRate = (pattern.positiveRatings / pattern.totalUses) * 100;
                pattern.patternType = pattern.successRate >= 70 ? 'successful' : 
                                     pattern.successRate <= 30 ? 'failed' : 'improved';
                pattern.lastUsed = Date.now();
                pattern.updatedAt = Date.now();
            }
            
            // Save to cache and Firebase
            this.knowledgeCache.set(patternId, pattern);
            await setDoc(doc(db, 'knowledgePatterns', patternId), pattern);
            
        } catch (error) {
            console.error('[AILearning] Error updating knowledge patterns:', error);
        }
    }
    
    /**
     * Get relevant knowledge patterns for a query
     */
    async getRelevantPatterns(query: string, domain: string): Promise<KnowledgePattern[]> {
        const patterns: KnowledgePattern[] = [];
        
        this.knowledgeCache.forEach(pattern => {
            if (pattern.domain === domain || pattern.domain === 'GENERAL') {
                // Simple relevance check - could be enhanced with embeddings
                const queryLower = query.toLowerCase();
                const patternLower = pattern.queryPattern.toLowerCase();
                
                if (this.calculateSimilarity(queryLower, patternLower) > 0.3) {
                    patterns.push(pattern);
                }
            }
        });
        
        // Sort by success rate
        return patterns.sort((a, b) => b.successRate - a.successRate).slice(0, 5);
    }
    
    /**
     * Get learnings to integrate into prompts (ENHANCED v2.0)
     * Provides actionable insights based on collected feedback
     */
    async getLearningsForPrompt(domain: string): Promise<string> {
        const patterns = Array.from(this.knowledgeCache.values())
            .filter(p => (p.domain === domain || p.domain === 'GENERAL') && p.totalUses >= 3)
            .sort((a, b) => b.successRate - a.successRate);
        
        if (patterns.length === 0) return '';
        
        // Get successful patterns (what works)
        const successfulPatterns = patterns.filter(p => p.successRate >= 70).slice(0, 3);
        const failedPatterns = patterns.filter(p => p.successRate < 40).slice(0, 2);
        
        let learnings = '';
        
        if (successfulPatterns.length > 0) {
            const effective = successfulPatterns.flatMap(p => p.effectiveApproaches).filter(Boolean);
            if (effective.length > 0) {
                learnings += `
**USER FEEDBACK LEARNINGS (${domain}):**
✅ WHAT USERS APPRECIATE:
${effective.slice(0, 5).map((l, i) => `${i + 1}. ${l}`).join('\n')}
`;
            }
        }
        
        if (failedPatterns.length > 0) {
            const avoidances = failedPatterns.flatMap(p => p.avoidances).filter(Boolean);
            if (avoidances.length > 0) {
                learnings += `
❌ AVOID THESE PATTERNS:
${avoidances.slice(0, 3).map((l, i) => `${i + 1}. ${l}`).join('\n')}
`;
            }
        }
        
        // Add domain-specific success rate
        const domainPatterns = patterns.filter(p => p.domain === domain);
        if (domainPatterns.length >= 5) {
            const avgSuccess = domainPatterns.reduce((sum, p) => sum + p.successRate, 0) / domainPatterns.length;
            learnings += `
📊 Domain satisfaction: ${avgSuccess.toFixed(0)}% (based on ${domainPatterns.length} interactions)
`;
        }
        
        return learnings;
    }
    
    /**
     * Enhanced feedback processing with learnings extraction (v2.0)
     */
    async extractLearningsFromFeedback(
        query: string,
        response: string,
        rating: 'positive' | 'negative',
        domain: string
    ): Promise<{ effectiveApproaches: string[], avoidances: string[] }> {
        const effectiveApproaches: string[] = [];
        const avoidances: string[] = [];
        
        // Analyze response structure
        const hasSteps = /step\s*\d|step\s*[a-z]:|first|second|third/i.test(response);
        const hasExamples = /example|for instance|such as|e\.g\.|consider/i.test(response);
        const hasCitations = /pfrs|pas\s*\d|nirc|section|article/i.test(response);
        const hasComputations = /=\s*[\d₱,]+|\d+\s*[×÷+\-]\s*\d+/i.test(response);
        const hasTables = /\|[^|]+\|/.test(response);
        const hasWarnings = /⚠️|trap|common mistake|avoid/i.test(response);
        
        if (rating === 'positive') {
            if (hasSteps) effectiveApproaches.push('Step-by-step explanations are appreciated');
            if (hasExamples) effectiveApproaches.push('Concrete examples help understanding');
            if (hasCitations) effectiveApproaches.push('Specific standard citations add credibility');
            if (hasComputations) effectiveApproaches.push('Clear computation formatting is valued');
            if (hasTables) effectiveApproaches.push('Tables for comparisons are helpful');
            if (hasWarnings) effectiveApproaches.push('Trap alerts and warnings are useful');
        } else {
            if (!hasSteps && query.length > 50) avoidances.push('Long responses need step-by-step structure');
            if (!hasExamples && /explain|how|what/i.test(query)) avoidances.push('Add practical examples for explanations');
            if (!hasCitations && /standard|pfrs|pas|rule/i.test(query)) avoidances.push('Include specific standard citations');
            if (!hasComputations && /calculat|compute|how much/i.test(query)) avoidances.push('Show detailed calculations');
        }
        
        return { effectiveApproaches, avoidances };
    }
    
    // ==================== Metrics ====================
    
    /**
     * Update overall metrics
     */
    private async updateMetrics(record: ResponseQuality): Promise<void> {
        try {
            const metricsRef = doc(db, 'aiMetrics', 'overall');
            const metricsDoc = await getDoc(metricsRef);
            
            if (metricsDoc.exists()) {
                const updates: any = {
                    totalResponses: increment(1),
                    [`domainPerformance.${record.domain}.responses`]: increment(1)
                };
                
                if (record.rating === 'positive') {
                    updates.positiveRatings = increment(1);
                } else if (record.rating === 'negative') {
                    updates.negativeRatings = increment(1);
                } else {
                    updates.neutralRatings = increment(1);
                }
                
                await updateDoc(metricsRef, updates);
            } else {
                // Initialize metrics
                const newMetrics: AIImprovementMetrics = {
                    totalResponses: 1,
                    positiveRatings: record.rating === 'positive' ? 1 : 0,
                    negativeRatings: record.rating === 'negative' ? 1 : 0,
                    neutralRatings: record.rating === 'neutral' ? 1 : 0,
                    satisfactionRate: record.rating === 'positive' ? 100 : 0,
                    domainPerformance: {
                        [record.domain]: {
                            responses: 1,
                            satisfaction: record.rating === 'positive' ? 100 : 0,
                            avgScore: 0
                        }
                    },
                    improvementsMade: 0,
                    learningsApplied: 0,
                    weeklyTrend: 0,
                    monthlyTrend: 0
                };
                
                await setDoc(metricsRef, newMetrics);
            }
        } catch (error) {
            console.error('[AILearning] Error updating metrics:', error);
        }
    }
    
    /**
     * Get current AI improvement metrics
     */
    async getMetrics(): Promise<AIImprovementMetrics | null> {
        // Cache for 5 minutes
        if (this.metricsCache && Date.now() - this.lastMetricsUpdate < 300000) {
            return this.metricsCache;
        }
        
        try {
            const metricsDoc = await getDoc(doc(db, 'aiMetrics', 'overall'));
            if (metricsDoc.exists()) {
                this.metricsCache = metricsDoc.data() as AIImprovementMetrics;
                this.lastMetricsUpdate = Date.now();
                return this.metricsCache;
            }
        } catch (error) {
            console.error('[AILearning] Error getting metrics:', error);
        }
        
        return null;
    }
    
    // ==================== v2.0 Enhanced Insights System ====================
    
    /**
     * Generate insights from collected data
     */
    private async generateInsights(): Promise<void> {
        // Run every 5 minutes
        setInterval(async () => {
            await this.analyzePatterns();
            await this.detectTrends();
            await this.identifyImprovementOpportunities();
        }, 5 * 60 * 1000);
    }
    
    /**
     * Analyze patterns in feedback
     */
    private async analyzePatterns(): Promise<void> {
        try {
            // Find domains with consistent issues
            const domainStats: Record<string, { positive: number; negative: number; neutral: number }> = {};
            
            this.recentFeedback.forEach(f => {
                if (!domainStats[f.domain]) {
                    domainStats[f.domain] = { positive: 0, negative: 0, neutral: 0 };
                }
                domainStats[f.domain][f.rating]++;
            });
            
            // Check for domains needing attention
            for (const [domain, stats] of Object.entries(domainStats)) {
                const total = stats.positive + stats.negative + stats.neutral;
                if (total >= 10) {
                    const successRate = stats.positive / total;
                    
                    if (successRate < 0.5) {
                        const existing = this.insightCache.find(
                            i => i.domain === domain && i.type === 'pattern' && !i.resolved
                        );
                        
                        if (!existing) {
                            const insight: LearningInsight = {
                                id: `insight_pattern_${domain}_${Date.now()}`,
                                type: 'pattern',
                                severity: successRate < 0.3 ? 'critical' : 'high',
                                domain,
                                description: `${domain} has ${((1 - successRate) * 100).toFixed(0)}% issue rate over ${total} responses`,
                                actionable: `Review ${domain} knowledge patterns and update response templates`,
                                metrics: { successRate, total, negative: stats.negative },
                                timestamp: Date.now(),
                                resolved: false
                            };
                            
                            this.insightCache.push(insight);
                            await setDoc(doc(db, 'learningInsights', insight.id), insight);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('[AILearning v2.0] Error analyzing patterns:', error);
        }
    }
    
    /**
     * Detect trends in feedback
     */
    private async detectTrends(): Promise<void> {
        try {
            const now = Date.now();
            const oneHourAgo = now - 60 * 60 * 1000;
            const twoHoursAgo = now - 2 * 60 * 60 * 1000;
            
            // Compare recent hour to previous hour
            const recentHour = this.recentFeedback.filter(f => f.timestamp > oneHourAgo);
            const previousHour = this.recentFeedback.filter(
                f => f.timestamp > twoHoursAgo && f.timestamp <= oneHourAgo
            );
            
            if (recentHour.length >= 5 && previousHour.length >= 5) {
                const recentPositive = recentHour.filter(f => f.rating === 'positive').length / recentHour.length;
                const previousPositive = previousHour.filter(f => f.rating === 'positive').length / previousHour.length;
                
                const change = recentPositive - previousPositive;
                
                if (Math.abs(change) > 0.2) {
                    const insight: LearningInsight = {
                        id: `insight_trend_${Date.now()}`,
                        type: 'trend',
                        severity: Math.abs(change) > 0.4 ? 'high' : 'medium',
                        domain: 'ALL',
                        description: change > 0 
                            ? `Satisfaction improving: ${(change * 100).toFixed(0)}% increase in last hour`
                            : `Satisfaction declining: ${(Math.abs(change) * 100).toFixed(0)}% decrease in last hour`,
                        actionable: change > 0 
                            ? 'Recent changes are working well, continue current approach'
                            : 'Investigate recent changes that may have impacted quality',
                        metrics: { change, recentRate: recentPositive, previousRate: previousPositive },
                        timestamp: Date.now(),
                        resolved: false
                    };
                    
                    this.insightCache.push(insight);
                    await setDoc(doc(db, 'learningInsights', insight.id), insight);
                }
            }
        } catch (error) {
            console.error('[AILearning v2.0] Error detecting trends:', error);
        }
    }
    
    /**
     * Identify improvement opportunities
     */
    private async identifyImprovementOpportunities(): Promise<void> {
        try {
            // Find query types with low success rates
            const queryTypeStats: Record<string, { success: number; total: number }> = {};
            
            this.knowledgeCache.forEach(pattern => {
                const queryType = this.classifyQueryType(pattern.queryPattern);
                if (!queryTypeStats[queryType]) {
                    queryTypeStats[queryType] = { success: 0, total: 0 };
                }
                queryTypeStats[queryType].total += pattern.totalUses;
                queryTypeStats[queryType].success += pattern.positiveRatings;
            });
            
            for (const [queryType, stats] of Object.entries(queryTypeStats)) {
                if (stats.total >= 20) {
                    const successRate = stats.success / stats.total;
                    
                    if (successRate < 0.6) {
                        const existing = this.insightCache.find(
                            i => i.description.includes(queryType) && i.type === 'improvement' && !i.resolved
                        );
                        
                        if (!existing) {
                            const insight: LearningInsight = {
                                id: `insight_improve_${queryType}_${Date.now()}`,
                                type: 'improvement',
                                severity: 'medium',
                                domain: 'ALL',
                                description: `"${queryType}" queries have ${(successRate * 100).toFixed(0)}% success rate`,
                                actionable: `Improve response templates for ${queryType} query types. Consider adding more examples or restructuring responses.`,
                                metrics: { successRate, total: stats.total },
                                timestamp: Date.now(),
                                resolved: false
                            };
                            
                            this.insightCache.push(insight);
                            await setDoc(doc(db, 'learningInsights', insight.id), insight);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('[AILearning v2.0] Error identifying improvements:', error);
        }
    }
    
    /**
     * Get active insights
     */
    getActiveInsights(): LearningInsight[] {
        return this.insightCache.filter(i => !i.resolved)
            .sort((a, b) => {
                const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                return severityOrder[a.severity] - severityOrder[b.severity];
            });
    }
    
    /**
     * Mark insight as resolved
     */
    async resolveInsight(insightId: string): Promise<void> {
        const insight = this.insightCache.find(i => i.id === insightId);
        if (insight) {
            insight.resolved = true;
            await updateDoc(doc(db, 'learningInsights', insightId), { resolved: true });
        }
    }
    
    /**
     * Get processing statistics
     */
    getProcessingStats() {
        return { ...this.processingStats };
    }
    
    /**
     * Get best response template for a query
     */
    getBestTemplate(domain: string, queryType: string): ResponseTemplate | null {
        const templateId = `template_${domain}_${queryType}`;
        let template = this.responseTemplateCache.get(templateId);
        
        if (!template) {
            // Try general template
            template = this.responseTemplateCache.get(`template_GENERAL_${queryType}`);
        }
        
        return template || null;
    }
    
    /**
     * Get enhanced prompt with learnings and personalization
     */
    async getEnhancedPromptContext(
        userId: string,
        domain: string,
        query: string
    ): Promise<string> {
        const learnings = await this.getLearningsForPrompt(domain);
        const recommendations = await this.getPersonalizedRecommendations(userId, domain);
        const queryType = this.classifyQueryType(query);
        const template = this.getBestTemplate(domain, queryType);
        
        let context = '';
        
        if (learnings) {
            context += `\n${learnings}\n`;
        }
        
        if (recommendations && recommendations !== '• No specific preferences detected yet') {
            context += `\n**USER PREFERENCES:**\n${recommendations}\n`;
        }
        
        if (template && template.successRate >= 70) {
            context += `\n**RECOMMENDED RESPONSE STRUCTURE (${(template.successRate).toFixed(0)}% success rate):**\n`;
            if (template.bestPractices.length > 0) {
                context += `✅ Best practices: ${template.bestPractices.slice(0, 3).join(', ')}\n`;
            }
            if (template.antiPatterns.length > 0) {
                context += `❌ Avoid: ${template.antiPatterns.slice(0, 2).join(', ')}\n`;
            }
        }
        
        return context;
    }
    
    // ==================== Utility Methods ====================
    
    private hashQuery(query: string): string {
        // Simple hash for pattern matching
        return query.toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .substring(0, 20);
    }
    
    private extractQueryPattern(query: string): string {
        // Extract key concepts from query
        return query.substring(0, 200);
    }
    
    private extractResponsePattern(response: string): string {
        // Extract structure pattern from response
        return response.substring(0, 500);
    }
    
    private calculateSimilarity(a: string, b: string): number {
        // Enhanced similarity: keyword extraction + weighted Jaccard
        const stopWords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'shall', 'can', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'and', 'but', 'or', 'not', 'no', 'nor', 'so', 'yet', 'both', 'either', 'neither', 'each', 'every', 'all', 'any', 'few', 'more', 'most', 'other', 'some', 'such', 'than', 'too', 'very', 'just', 'about', 'it', 'its', 'this', 'that', 'these', 'those', 'i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'him', 'his', 'she', 'her', 'they', 'them', 'their', 'what', 'which', 'who', 'whom', 'how', 'when', 'where', 'why']);

        const extractKeywords = (text: string): Set<string> => {
            const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));
            return new Set(words);
        };

        const keywordsA = extractKeywords(a);
        const keywordsB = extractKeywords(b);

        if (keywordsA.size === 0 || keywordsB.size === 0) return 0;

        const intersection = new Set([...keywordsA].filter(x => keywordsB.has(x)));
        const union = new Set([...keywordsA, ...keywordsB]);

        // Weighted: domain-specific terms count double
        const domainTerms = new Set(['pfrs', 'pas', 'nirc', 'tax', 'audit', 'depreciation', 'amortization', 'consolidation', 'partnership', 'liquidation', 'variance', 'budget', 'vat', 'rcit', 'mcit', 'journal', 'entry', 'reconciliation', 'impairment', 'revaluation', 'provision', 'contingent', 'lease', 'revenue', 'contract', 'obligation', 'corporation', 'irac']);
        let weightedIntersection = intersection.size;
        intersection.forEach(word => {
            if (domainTerms.has(word)) weightedIntersection += 0.5;
        });

        return weightedIntersection / union.size;
    }
    
    /**
     * Export learning data for analysis
     */
    async exportLearningData(): Promise<{
        patterns: KnowledgePattern[];
        insights: LearningInsight[];
        templates: ResponseTemplate[];
        profiles: UserLearningProfile[];
        stats: typeof this.processingStats;
    }> {
        return {
            patterns: Array.from(this.knowledgeCache.values()),
            insights: this.insightCache,
            templates: Array.from(this.responseTemplateCache.values()),
            profiles: Array.from(this.userProfileCache.values()),
            stats: this.processingStats
        };
    }
    
    /**
     * Clear old data to maintain performance
     */
    async cleanupOldData(daysToKeep: number = 30): Promise<void> {
        const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
        
        // Clean up old insights
        const oldInsights = this.insightCache.filter(i => i.timestamp < cutoffTime && i.resolved);
        for (const insight of oldInsights) {
            try {
                await deleteDoc(doc(db, 'learningInsights', insight.id));
            } catch (e) {
                console.error('[AILearning v2.0] Error deleting old insight:', e);
            }
        }
        
        this.insightCache = this.insightCache.filter(i => i.timestamp >= cutoffTime || !i.resolved);
        
        console.log(`[AILearning v2.0] Cleaned up ${oldInsights.length} old insights`);
    }
}

// Export singleton
export const aiLearningService = AILearningService.getInstance();

// Export functions - v2.0 Enhanced
export const processFeedback = (
    responseId: string,
    userQuery: string,
    aiResponse: string,
    rating: 'positive' | 'negative' | 'neutral',
    feedbackText?: string,
    metadata?: any
) => aiLearningService.processFeedback(responseId, userQuery, aiResponse, rating, feedbackText, metadata);

export const getRelevantPatterns = (query: string, domain: string) => 
    aiLearningService.getRelevantPatterns(query, domain);

export const getLearningsForPrompt = (domain: string) => 
    aiLearningService.getLearningsForPrompt(domain);

export const getAIMetrics = () => aiLearningService.getMetrics();

// v2.0 New exports
export const getUserProfile = (userId: string) =>
    aiLearningService.getUserProfile(userId);

export const getPersonalizedRecommendations = (userId: string, domain: string) =>
    aiLearningService.getPersonalizedRecommendations(userId, domain);

export const getActiveInsights = () =>
    aiLearningService.getActiveInsights();

export const resolveInsight = (insightId: string) =>
    aiLearningService.resolveInsight(insightId);

export const getProcessingStats = () =>
    aiLearningService.getProcessingStats();

export const getEnhancedPromptContext = (userId: string, domain: string, query: string) =>
    aiLearningService.getEnhancedPromptContext(userId, domain, query);

export const exportLearningData = () =>
    aiLearningService.exportLearningData();

export const cleanupOldLearningData = (daysToKeep?: number) =>
    aiLearningService.cleanupOldData(daysToKeep);

